/**
 * Conversation endpoints — list / get / delete history.
 *
 * Ports v1:
 *   - ``ando-frontend/app/api/loadConversations.ts`` (lines 1-25)
 *   - ``ando-frontend/app/api/loadConversation.ts`` (lines 1-28)
 *   - ``ando-frontend/app/api/deleteConversation.ts`` (lines 1-27)
 *
 * v1 wrapped each call in a try/catch and returned a ``{ ..., error: string }``
 * fallback. v2 lets the ``ApiError`` propagate — React Query handles
 * retries + error UI uniformly. Keeping per-call try/catch in the helper
 * would defeat the point of the unified error hierarchy.
 */

import { apiClient } from "../ApiClient.js";

// ---------------------------------------------------------------------------
// Types — wire shapes, kept loose. Feature slices refine via zod at the boundary.
//
// BE source: `src/api/v1/endpoints/conversations.py::get_conversation_history`.
// Wire shape (v1 parity, see `ando-frontend/app/types/payloads.ts:54-61`):
//   ``{ trees: { [conversation_id]: { title, summary, created_at,
//                                     last_update_time } }, error }``
// The conversation_id is the OUTER KEY, not a field on the value.
// ---------------------------------------------------------------------------

/**
 * One sidebar entry value. The conversation_id lives in the OUTER KEY of
 * the ``trees`` map — callers MUST close over the key when projecting.
 */
export interface ConversationSummary {
  title: string;
  /** BE: ``conversations.summary`` column — short body preview. May be empty. */
  summary?: string | null;
  /** ISO timestamp. May be undefined on freshly-created rows. */
  created_at?: string | null;
  /**
   * BE field name (NOT ``updated_at``). Derived from
   * ``last_message_at`` ?? ``updated_at`` ?? ``created_at`` server-side.
   */
  last_update_time: string;
  /**
   * Owning project (May 22, 2026). ``null`` for legacy / unassigned rows
   * — the sidebar groups those under "Senza progetto". Optional in the
   * wire shape because the BE migration adding the column is rolling out
   * gradually; once every row carries it we can tighten the type.
   */
  project_id?: string | null;
}

export interface ConversationHistoryPayload {
  trees: Record<string, ConversationSummary>;
  error?: string;
}

export interface ConversationDetail {
  /**
   * BE returns ``rebuild`` (message tree), ``decision_trees`` and
   * ``feedback_map`` (see ``get_conversation_with_messages``). The feature
   * slice refines the inner shapes via zod at the chat-store boundary.
   */
  rebuild: unknown[];
  decision_trees?: unknown[];
  feedback_map?: Record<string, number>;
  config?: unknown;
  error?: string;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET ``/conversations/history/{user_id}`` — list summaries for sidebar. */
export async function listConversations(userId: string): Promise<ConversationHistoryPayload> {
  return await apiClient.get<ConversationHistoryPayload>(
    `/conversations/history/${encodeURIComponent(userId)}`,
  );
}

/** GET ``/conversations/history/{user_id}/{conversation_id}`` — full message tree. */
export async function getConversation(
  userId: string,
  conversationId: string,
): Promise<ConversationDetail> {
  return await apiClient.get<ConversationDetail>(
    `/conversations/history/${encodeURIComponent(userId)}/${encodeURIComponent(conversationId)}`,
  );
}

/** DELETE ``/conversations/{user_id}/{conversation_id}``. */
export async function deleteConversation(
  userId: string,
  conversationId: string,
): Promise<{ ok: boolean }> {
  return await apiClient.delete<{ ok: boolean }>(
    `/conversations/${encodeURIComponent(userId)}/${encodeURIComponent(conversationId)}`,
  );
}

/**
 * Patch shape — every field is optional and only sent when the caller
 * wants to update it. Mirrors the BE ``UpdateConversationRequest``.
 */
export interface UpdateConversationInput {
  readonly title?: string;
  readonly summary?: string;
  readonly folder_filter?: ReadonlyArray<string>;
}

/**
 * PATCH ``/conversations/{user_id}/{conversation_id}`` — persist a
 * metadata change (rename, folder filter swap, summary edit). Returns
 * the BE's success envelope, but callers usually just read ``.title``
 * back to confirm round-trip OK.
 */
export async function updateConversation(
  userId: string,
  conversationId: string,
  patch: UpdateConversationInput,
): Promise<Record<string, unknown>> {
  return await apiClient.patch<Record<string, unknown>>(
    `/conversations/${encodeURIComponent(userId)}/${encodeURIComponent(conversationId)}`,
    patch,
  );
}
